/* eslint-disable max-lines-per-function */
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { uploadStaticAssets } from '@atlassian/mpac-api-client';
import { ImageField, TextInput } from '@atlassian/mpac-ui';
import React from 'react';

import type { ContentProps, MultiStepInitialContent } from '@atlassian/mpac-partners';
import type { FeaturedTileConfigurationType } from '@atlassian/mpac-types';

const flexStyles = xcss({
  gap: 'space.300',
});

const inputBoxStyles = xcss({
  flex: 1,
  gap: 'space.400',
});

const imageBoxStyles = xcss({
  flex: 1,
  gap: 'space.300',
});

type Props = {
  value: MultiStepInitialContent & FeaturedTileConfigurationType;
  onFieldChange: (fieldName: string, id: string) => (newValue: string) => void;
  isDisabled?: boolean;
};

const FeaturedTileConfig = (props: Props) => {
  const { value, onFieldChange, isDisabled } = props;

  const handleImageFieldChange = (fieldName: string, id: string) => (e: { imageUrl?: string }) => {
    onFieldChange(fieldName, id)(e.imageUrl || '');
  };

  const getImageFieldUrl = (imageUrl?: string) => (imageUrl ? { imageUrl } : {});

  return (
    <Flex xcss={flexStyles}>
      <Box xcss={imageBoxStyles}>
        <ImageField
          label="Tile Image"
          name={`image_${value.id}`}
          customFileName="tileImage"
          uploadFileHandler={uploadStaticAssets}
          url={getImageFieldUrl(value.image)}
          onChange={handleImageFieldChange('image', value.id)}
          enableImageDrop
          imageType="title-logo"
          isDisabled={isDisabled}
          data-testid="image-field"
          isRequired
        />
      </Box>
      <Box xcss={inputBoxStyles}>
        <TextInput
          label="Title"
          placeholder="Title for the tile"
          isRequired
          defaultValue={value.title}
          name={`title_${value.id}`}
          onChange={onFieldChange('title', value.id)}
          applyDefaultValidation
        />
        <TextInput
          label="Description"
          placeholder="description for the tile"
          isRequired
          defaultValue={value.description}
          name={`description_${value.id}`}
          onChange={onFieldChange('description', value.id)}
          applyDefaultValidation
        />
        <TextInput
          label="Redirection Link"
          placeholder="Redirection URL for click on tile"
          isRequired
          defaultValue={value.redirectUrl}
          name={`redirectUrl_${value.id}`}
          onChange={onFieldChange('redirectUrl', value.id)}
          applyDefaultValidation
        />
        <TextInput
          label="Feature Gate"
          placeholder="Feature Gate for the tile visibility"
          defaultValue={value.featureGate}
          name={`featureGate_${value.id}`}
          onChange={onFieldChange('featureGate', value.id)}
          applyDefaultValidation
        />
      </Box>
    </Flex>
  );
};

// Wrapper function to adapt ContentProps to Props
const FeaturedTileConfigWrapper = (props: ContentProps) => {
  const adaptedProps: Props = {
    ...props,
    value: props.value as MultiStepInitialContent & FeaturedTileConfigurationType,
  };
  return <FeaturedTileConfig {...adaptedProps} />;
};

export default FeaturedTileConfigWrapper;
